import React, { useState, useEffect } from 'react';

/**
 * Composant de gestion des Templates Email - Version Professionnelle
 * Permet de créer, modifier et prévisualiser des templates email par type de document
 * Respecte la charte graphique Baobab Connect
 */
const EmailTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeDocType, setActiveDocType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    document_type: 'devis',
    subject: '',
    body: '',
    is_default: false
  });

  // Types de documents disponibles
  const documentTypes = [
    { id: 'devis', label: 'Devis', icon: '📄', color: 'bg-blue-500', lightColor: 'bg-blue-50', textColor: 'text-blue-700' },
    { id: 'facture', label: 'Facture', icon: '🧾', color: 'bg-green-500', lightColor: 'bg-green-50', textColor: 'text-green-700' },
    { id: 'bon_commande', label: 'Bon de commande', icon: '📋', color: 'bg-purple-500', lightColor: 'bg-purple-50', textColor: 'text-purple-700' },
    { id: 'proforma', label: 'Proforma', icon: '📝', color: 'bg-orange-500', lightColor: 'bg-orange-50', textColor: 'text-orange-700' },
    { id: 'relance', label: 'Relance paiement', icon: '⏰', color: 'bg-red-500', lightColor: 'bg-red-50', textColor: 'text-red-700' },
    { id: 'confirmation', label: 'Confirmation', icon: '✅', color: 'bg-teal-500', lightColor: 'bg-teal-50', textColor: 'text-teal-700' }
  ];

  // Variables disponibles avec descriptions et exemples
  const availableVariables = [
    { var: '{{client_name}}', desc: 'Nom du client', example: 'ACME Corporation' },
    { var: '{{document_type}}', desc: 'Type de document', example: 'Devis' },
    { var: '{{document_reference}}', desc: 'Référence du document', example: 'DEV-BC-2601-0042' },
    { var: '{{montant_total}}', desc: 'Montant TTC', example: '1 500 000 F CFA' },
    { var: '{{date_creation}}', desc: 'Date de création', example: '07/01/2026' },
    { var: '{{validite}}', desc: 'Validité du devis', example: '10 jours' },
    { var: '{{objet}}', desc: 'Objet du document', example: 'Transport de marchandises' },
    { var: '{{user_name}}', desc: "Nom de l'expéditeur", example: 'Jean Dupont' }
  ];

  // Modèles par défaut professionnels
  const defaultTemplates = {
    devis: {
      name: 'Envoi de devis standard',
      subject: '{{document_type}} n°{{document_reference}} - {{client_name}}',
      body: `Bonjour {{client_name}},

Nous avons le plaisir de vous transmettre notre {{document_type}} n°{{document_reference}}.

Détails :
• Montant total : {{montant_total}}
• Validité : {{validite}}
• Objet : {{objet}}

Vous trouverez le document PDF en pièce jointe de cet email.

Nous restons à votre entière disposition pour toute question ou complément d'information.

Cordialement,
{{user_name}}
Baobab Connect`
    },
    facture: {
      name: 'Envoi de facture',
      subject: 'Facture n°{{document_reference}} - {{client_name}}',
      body: `Bonjour {{client_name}},

Veuillez trouver ci-joint la facture n°{{document_reference}} relative à votre commande.

Détails :
• Montant TTC : {{montant_total}}
• Date : {{date_creation}}

Le document PDF est joint à cet email.

Merci de procéder au règlement dans les délais convenus.

Cordialement,
{{user_name}}
Baobab Connect`
    },
    bon_commande: {
      name: 'Confirmation de commande',
      subject: 'Bon de commande n°{{document_reference}} - {{client_name}}',
      body: `Bonjour {{client_name}},

Nous vous confirmons la réception de votre commande n°{{document_reference}}.

Récapitulatif :
• Montant total : {{montant_total}}
• Date : {{date_creation}}

Le bon de commande est joint à cet email.

Cordialement,
{{user_name}}
Baobab Connect`
    },
    relance: {
      name: 'Relance de paiement',
      subject: 'Relance - Facture n°{{document_reference}}',
      body: `Bonjour {{client_name}},

Nous nous permettons de vous relancer concernant la facture n°{{document_reference}} d'un montant de {{montant_total}}.

Sauf erreur de notre part, cette facture reste impayée à ce jour.

Nous vous remercions de procéder au règlement dans les meilleurs délais.

En cas de paiement déjà effectué, veuillez ignorer ce message.

Cordialement,
{{user_name}}
Baobab Connect`
    }
  };

  // Charger les templates
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingTemplate 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/email-templates/${editingTemplate.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/email-templates`;
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchTemplates();
        resetForm();
        alert(editingTemplate ? 'Template mis à jour avec succès !' : 'Template créé avec succès !');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/email-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        await fetchTemplates();
        alert('Template supprimé avec succès');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      document_type: template.document_type,
      subject: template.subject,
      body: template.body,
      is_default: template.is_default || false
    });
    setShowForm(true);
  };

  const handleDuplicate = (template) => {
    setEditingTemplate(null);
    setFormData({
      name: `${template.name} (copie)`,
      document_type: template.document_type,
      subject: template.subject,
      body: template.body,
      is_default: false
    });
    setShowForm(true);
  };

  const handleSetDefault = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/email-templates/${templateId}/set-default`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      await fetchTemplates();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      document_type: 'devis',
      subject: '',
      body: '',
      is_default: false
    });
    setEditingTemplate(null);
    setShowForm(false);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = formData.body.substring(0, start) + variable + formData.body.substring(end);
      setFormData({ ...formData, body: newBody });
      
      // Repositionner le curseur après la variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setFormData({ ...formData, body: formData.body + variable });
    }
  };

  const applyDefaultTemplate = (docType) => {
    const template = defaultTemplates[docType];
    if (template) {
      setFormData({
        ...formData,
        name: template.name,
        document_type: docType,
        subject: template.subject,
        body: template.body
      });
    }
  };

  // Prévisualisation avec remplacement des variables
  const getPreviewContent = (content) => {
    let preview = content || '';
    const sampleData = {
      '{{client_name}}': 'ACME Corporation',
      '{{document_type}}': 'Devis',
      '{{document_reference}}': 'DEV-BC-2601-0042',
      '{{montant_total}}': '1 500 000 F CFA',
      '{{date_creation}}': '07/01/2026',
      '{{validite}}': '10 jours',
      '{{objet}}': 'Transport de marchandises',
      '{{user_name}}': 'Jean Dupont'
    };
    
    for (const [variable, value] of Object.entries(sampleData)) {
      preview = preview.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), 
        `<span class="bg-teal-100 text-teal-800 px-1 rounded">${value}</span>`);
    }
    
    return preview;
  };

  const getDocTypeInfo = (typeId) => {
    return documentTypes.find(t => t.id === typeId) || { icon: '📧', label: typeId, color: 'bg-gray-500', lightColor: 'bg-gray-50', textColor: 'text-gray-700' };
  };

  // Filtrage des templates
  const filteredTemplates = templates.filter(t => {
    const matchesType = activeDocType === 'all' || t.document_type === activeDocType;
    const matchesSearch = !searchTerm || 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.subject.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Grouper les templates par type
  const groupedTemplates = documentTypes.reduce((acc, type) => {
    acc[type.id] = filteredTemplates.filter(t => t.document_type === type.id);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <span className="text-gray-600 font-medium">Chargement des templates...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <span className="bg-white/20 p-2 rounded-lg">📧</span>
              Templates Email
            </h2>
            <p className="text-teal-100 mt-1">
              Personnalisez vos emails pour une communication professionnelle
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold">{templates.length}</div>
              <div className="text-teal-200 text-sm">Templates</div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-white text-teal-600 px-5 py-2.5 rounded-lg hover:bg-teal-50 transition-all font-semibold shadow-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouveau Template
            </button>
          </div>
        </div>
      </div>

      {/* Barre de filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Recherche */}
          <div className="relative flex-1 min-w-[200px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher un template..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
            />
          </div>
          
          {/* Filtres par type */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveDocType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeDocType === 'all'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tous ({templates.length})
            </button>
            {documentTypes.map((type) => {
              const count = templates.filter(t => t.document_type === type.id).length;
              if (count === 0) return null;
              return (
                <button
                  key={type.id}
                  onClick={() => setActiveDocType(type.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    activeDocType === type.id
                      ? `${type.color} text-white shadow-md`
                      : `${type.lightColor} ${type.textColor} hover:opacity-80`
                  }`}
                >
                  <span>{type.icon}</span>
                  <span>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Formulaire de création/édition */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              {editingTemplate ? (
                <>
                  <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Modifier le template
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Créer un nouveau template
                </>
              )}
            </h3>
            <button 
              onClick={resetForm} 
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-200 rounded"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            {/* Modèles prédéfinis */}
            {!editingTemplate && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <label className="block text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Démarrage rapide - Partir d'un modèle
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(defaultTemplates).map((typeId) => {
                    const typeInfo = getDocTypeInfo(typeId);
                    return (
                      <button
                        key={typeId}
                        type="button"
                        onClick={() => applyDefaultTemplate(typeId)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${typeInfo.lightColor} ${typeInfo.textColor} hover:shadow-md border border-transparent hover:border-current`}
                      >
                        <span>{typeInfo.icon}</span>
                        <span>{typeInfo.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Nom du template */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom du template <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Devis standard, Relance 1ère instance..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  />
                </div>

                {/* Type de document */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Type de document <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.document_type}
                    onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  >
                    {documentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Sujet de l'email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sujet de l'email <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ex: {{document_type}} n°{{document_reference}} - {{client_name}}"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all font-mono text-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Utilisez les variables pour personnaliser automatiquement le sujet</p>
              </div>

              {/* Variables disponibles */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Variables dynamiques (cliquez pour insérer)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableVariables.map((v) => (
                    <button
                      key={v.var}
                      type="button"
                      onClick={() => insertVariable(v.var)}
                      className="text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all group"
                    >
                      <div className="font-mono text-xs text-teal-600 group-hover:text-teal-700">{v.var}</div>
                      <div className="text-xs text-gray-500 truncate">{v.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Corps de l'email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Corps de l'email <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="template-body"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Rédigez votre email ici..."
                  rows={12}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono text-sm transition-all"
                  required
                />
              </div>

              {/* Template par défaut */}
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="is_default" className="flex-1">
                  <span className="font-medium text-amber-800">Définir comme template par défaut</span>
                  <p className="text-sm text-amber-600">Ce template sera automatiquement sélectionné lors de l'envoi d'un email de ce type</p>
                </label>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  type="button" 
                  onClick={resetForm} 
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-all"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTemplate({ ...formData })}
                  className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Aperçu
                </button>
                <button 
                  type="submit" 
                  className="px-5 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 font-medium transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editingTemplate ? 'Mettre à jour' : 'Créer le template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Aperçu */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
            {/* En-tête */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4 flex justify-between items-center">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Aperçu de l'email
              </h4>
              <button 
                onClick={() => setPreviewTemplate(null)} 
                className="text-white/80 hover:text-white transition-colors p-1 hover:bg-white/10 rounded"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Contenu simulé */}
            <div className="overflow-y-auto max-h-[calc(85vh-120px)]">
              {/* Simulation d'un email */}
              <div className="p-6 space-y-4">
                {/* En-tête email simulé */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">BC</div>
                    <div>
                      <div className="font-medium text-gray-900">Baobab Connect</div>
                      <div className="text-sm text-gray-500">facturation@baobabco.tech</div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="text-sm text-gray-500 mb-1">À: client@acme.com</div>
                    <div className="font-semibold text-gray-900" dangerouslySetInnerHTML={{ __html: getPreviewContent(previewTemplate.subject) }}></div>
                  </div>
                </div>

                {/* Corps de l'email */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div 
                    className="whitespace-pre-line text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: getPreviewContent(previewTemplate.body) }}
                  ></div>
                </div>

                {/* Pièce jointe simulée */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">PDF</div>
                  <div className="flex-1">
                    <div className="font-medium text-amber-800">DEV-BC-2601-0042.pdf</div>
                    <div className="text-sm text-amber-600">Document joint automatiquement</div>
                  </div>
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </div>

                {/* Légende */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-700">
                      <span className="font-medium">Variables remplacées : </span>
                      Les textes surlignés en <span className="bg-teal-100 text-teal-800 px-1 rounded">vert</span> seront automatiquement remplacés par les vraies valeurs lors de l'envoi.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des templates */}
      {filteredTemplates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">📧</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {searchTerm || activeDocType !== 'all' ? 'Aucun template trouvé' : 'Aucun template email créé'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || activeDocType !== 'all' 
              ? 'Essayez de modifier vos critères de recherche'
              : 'Créez votre premier template pour personnaliser vos emails'}
          </p>
          {!searchTerm && activeDocType === 'all' && (
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors font-medium"
            >
              Créer mon premier template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => {
            const typeInfo = getDocTypeInfo(template.document_type);
            return (
              <div 
                key={template.id} 
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all group"
              >
                {/* En-tête de la carte */}
                <div className={`${typeInfo.lightColor} px-4 py-3 border-b border-gray-100`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`${typeInfo.color} text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm`}>
                        {typeInfo.icon}
                      </span>
                      <span className={`text-sm font-medium ${typeInfo.textColor}`}>{typeInfo.label}</span>
                    </div>
                    {template.is_default && (
                      <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Défaut
                      </span>
                    )}
                  </div>
                </div>

                {/* Contenu de la carte */}
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 line-clamp-1">{template.name}</h4>
                  <p className="text-sm text-gray-500 mb-3 line-clamp-1 font-mono">{template.subject}</p>
                  <p className="text-xs text-gray-400 line-clamp-2 mb-4">
                    {template.body?.substring(0, 100)}...
                  </p>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex gap-1">
                      {!template.is_default && (
                        <button
                          onClick={() => handleSetDefault(template.id)}
                          className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Définir par défaut"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => setPreviewTemplate(template)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Aperçu"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-2 text-purple-500 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Dupliquer"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-teal-500 hover:bg-teal-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aide contextuelle */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div>
            <h5 className="font-semibold text-blue-900 mb-2">Comment utiliser les templates ?</h5>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Le <strong>document PDF</strong> est automatiquement généré et joint à chaque email envoyé
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Les <strong>variables</strong> comme <code className="bg-white px-1 rounded text-xs">{'{{client_name}}'}</code> sont remplacées automatiquement
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Le template <strong>par défaut</strong> est pré-sélectionné lors de l'envoi d'un email
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Utilisez l'<strong>aperçu</strong> pour vérifier le rendu avant d'envoyer
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesManager;
